import { useEffect, useState } from 'react';
import { Col, FormGroup, Label, Input } from 'reactstrap';
import { STATE_OPTIONS, PROVINCE_OPTIONS } from '../../utils/constants';
import { useUnifiedFetch } from '../../../src/hooks';

export default function Location({ disabled, boxDbLocation, getLocationInfo, latLang, address2 }) {
  const {
    isLoading: isFetchingPlaces,
    data: suggestions,
    fetchData: fetchGoogleData,
  } = useUnifiedFetch(`boxes/places-autocomplete`, false);

  const {
    isLoading: isFetchingDetails,
    data: addressDetails,
    fetchData: fetchAddressDetails,
  } = useUnifiedFetch(`boxes/address-details`, false);

  const [location, setLocation] = useState({
    name: '',
    city: '',
    state: '',
    address: '',
    address2: '',
    zip: '',
    lat: '',
    lng: '',
    country: 'United States',
    stateFieldName: 'State',
    zipFieldName: 'ZIP Code',
    stateProvinceList: STATE_OPTIONS,
  });

  const [query, setQuery] = useState('');
  const [placesSuggestions, setPlacesSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Initialize location state based on `boxDbLocation`
  useEffect(() => {
    if (boxDbLocation?.hasOwnProperty('address') && typeof boxDbLocation?.address === 'object') {
      setLocation((prevState) => ({
        ...prevState,
        city: boxDbLocation?.address?.city || '',
        state: boxDbLocation?.address?.state || '',
        address: boxDbLocation?.address?.address || '',
        address2: boxDbLocation?.address?.address2 || '',
        zip: boxDbLocation?.address?.zip || '',
        lat: boxDbLocation?.geoJson?.coordinates[1] || '',
        lng: boxDbLocation?.geoJson?.coordinates[0] || '',
      }));
    } else {
      setLocation((prevState) => ({
        ...prevState,
        city: boxDbLocation?.city || '',
        state: boxDbLocation?.state || '',
        address: boxDbLocation?.address1 || '',
        address2: boxDbLocation?.address2 || '',
        zip: boxDbLocation?.zip || '',
      }));
    }
  }, [boxDbLocation]);

  // Fetch suggestions when query changes
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setLocation((prevState) => ({ ...prevState, address: value }));

    if (value) {
      fetchGoogleData({ searchquery: value });
      setShowSuggestions(true);
    } else {
      setPlacesSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle address selection and fetch detailed data
  const handleSelect = async (placeId) => {
    const data = await fetchAddressDetails({ placeId });

    setLocation((prevState) => ({
      ...prevState,
      address: data?.address || '',
      address2: data?.address2 || '',
      city: data?.city || '',
      state: data?.state || '',
      zip: data?.zip || '',
      lat: data?.lat || '',
      lng: data?.lng || '',
      country: data?.country || 'United States',
    }));

    setQuery(data?.address || '');
    setShowSuggestions(false);

    checkIfStateOrProvince(data?.state);
  };

  // Update location details in parent
  useEffect(() => {
    const payload = { ...location };
    delete payload.stateFieldName;
    delete payload.zipFieldName;
    delete payload.stateProvinceList;
    delete payload.name;

    if (address2) {
      delete payload.lat;
      delete payload.lng;
    }
    if (!address2) delete payload.address2;

    getLocationInfo(payload);
  }, [location]);

  // Check if state is Canadian province
  const checkIfStateOrProvince = (value) => {
    const isProvince = PROVINCE_OPTIONS.some((province) => province.value === value);
    setLocation((prevState) => ({
      ...prevState,
      stateFieldName: isProvince ? 'Province' : 'State',
      zipFieldName: isProvince ? 'Postal Code' : 'ZIP Code',
      country: isProvince ? 'Canada' : 'United States',
      stateProvinceList: isProvince ? PROVINCE_OPTIONS : STATE_OPTIONS,
    }));
  };

  // Render suggestions
  useEffect(() => {
    if (suggestions?.predictions?.length) {
      setPlacesSuggestions(suggestions.predictions);
    } else {
      setPlacesSuggestions([]);
      setShowSuggestions(false);
    }
  }, [suggestions]);

  const { stateFieldName, zipFieldName, country, stateProvinceList } = location;

  return (
    <>
      <FormGroup row>
        <Label for="address" sm={4}>
          {address2 ? 'Address 1:' : 'Address:'}
        </Label>
        <Col sm={12}>
          <Input
            id="address"
            value={query || location?.address}
            onChange={handleInputChange}
            placeholder="Search for places"
          />
          {isFetchingPlaces && <div>Loading suggestions...</div>}
          {isFetchingDetails && <div>Fetching address details...</div>}
          {showSuggestions &&
            placesSuggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleSelect(suggestion.place_id)}
                className="react-autosuggest__suggestion locations"
              >
                <span>{suggestion.description}</span>
              </div>
            ))}
        </Col>
      </FormGroup>

      {address2 && (
        <FormGroup row>
          <Label for="address2" sm={4}>
            Address 2:
          </Label>
          <Col sm={12}>
            <Input
              id="address2"
              name="address2"
              value={location.address2}
              onChange={(e) =>
                setLocation((prevState) => ({ ...prevState, address2: e.target.value }))
              }
            />
          </Col>
        </FormGroup>
      )}

      <FormGroup row>
        <Label for="city" sm={4}>
          City:
        </Label>
        <Col sm={12}>
          <Input
            id="city"
            name="city"
            value={location.city}
            disabled={disabled}
            onChange={(e) =>
              setLocation((prevState) => ({ ...prevState, city: e.target.value }))
            }
          />
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label for="state" sm={4}>
          {stateFieldName}:
        </Label>
        <Col sm={12}>
          <Input
            id="state"
            name="state"
            type="select"
            value={location.state}
            disabled={disabled}
            onChange={(e) =>
              setLocation((prevState) => ({ ...prevState, state: e.target.value }))
            }
          >
            <option value="" hidden>
              Select {stateFieldName}
            </option>
            {stateProvinceList.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Input>
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label for="zip" sm={4}>
          {zipFieldName}:
        </Label>
        <Col sm={12}>
          <Input
            id="zip"
            name="zip"
            value={location.zip}
            disabled={disabled}
            onChange={(e) =>
              setLocation((prevState) => ({ ...prevState, zip: e.target.value }))
            }
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="zip" sm={4}>
          Country:
        </Label>
        <Col sm={12}>
          <Input
            disabled
            placeholder="Please specify"
            required
            type="text"
            name="zip"
            value={country}
          />
        </Col>
      </FormGroup>
    </>
  );
}
